import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigServiceProvider } from '../config-service/config-service';
import { StorageServiceProvider } from '../storage-service/storage-service';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { catchError, flatMap, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { TenantService } from '../tenant-service/tenant.service';
import { ENVIRONMENT } from '../../../../../environments/environment';

export interface AuthTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
  id_token: string;
  session_state: string;
  scope: string;
}

@Injectable()
export class AuthenticationServiceProvider {
  private _stateSubject: Subject<boolean> = new Subject<boolean>();
  readonly state$: Observable<boolean> = this._stateSubject.asObservable();
  refreshToken: string;

  constructor(
    private http: HttpClient,
    private configService: ConfigServiceProvider,
    private storageService: StorageServiceProvider,
    private tenantService: TenantService,
  ) {}

  public getAccessToken(): Promise<any> {
    return this.storageService
      .get('accessToken')
      .then((res) => {
        if (res === null) {
          return Promise.reject('[AuthService] Access token missing');
        }
        return res;
      })
      .catch((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.renewTokens()
            .toPromise()
            .then((res) => res);
        }
        throw err;
      });
  }

  public validateTokens(): Promise<void> {
    return this.storageService
      .get('accessToken')
      .then((res) => res === null && Promise.reject('access token missing'))
      .then(() => this.http.get(this.configService.getUrl('account')).toPromise())
      .then(() => null)
      .catch((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return this.renewTokens();
        }
        throw err;
      });
  }

  public exchangeCodeForToken(authorizationCode: string): Promise<void> {
    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('client_id', this.configService.AUTH_CLIENT_ID);
    urlEncodedBody.append('grant_type', 'authorization_code');
    urlEncodedBody.append('redirect_uri', ENVIRONMENT.authLoginRedirect.replace('{tenant}', this.tenantService.getTenant()));
    urlEncodedBody.append('code', authorizationCode);

    const tokenUrl = ENVIRONMENT.tokenUrl.replace('{tenant}', this.tenantService.getTenant());

    return this.http
      .post<AuthTokenResponse>(tokenUrl, urlEncodedBody.toString(), {
        headers: this.getXWWWFromHeader(),
        withCredentials: true,
      })
      .toPromise()
      .then((response) =>
        Promise.all([
          this.storageService.set('accessToken', response.access_token),
          this.storageService.set('idToken', response.id_token),
          this.storageService.set('refreshToken', response.refresh_token),
        ]),
      )
      .then(() => this._stateSubject.next(true));
  }

  public exchangeUserCredsForToken(user: { username: string; password: string }): Promise<void> {
    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('client_secret', this.configService.AUTH_CLIENT_SECRET);
    urlEncodedBody.append('client_id', this.configService.AUTH_CLIENT_ID);
    urlEncodedBody.append('grant_type', 'password');
    urlEncodedBody.append('username', user.username);
    urlEncodedBody.append('password', user.password);

    return this.http
      .post<any>(this.configService.TOKEN_URL, urlEncodedBody.toString(), {
        headers: this.getXWWWFromHeader(),
      })
      .toPromise()
      .then((response) =>
        Promise.all([this.storageService.set('accessToken', response.access_token), this.storageService.set('refreshToken', response.refresh_token)]),
      )
      .then(() => {
        this._stateSubject.next(true);
      });
  }

  public logout(): Promise<void> {
    return Promise.all([this.storageService.set('accessToken', null), this.storageService.set('refreshToken', null)]).then(() =>
      this._stateSubject.next(false),
    );
  }

  public resetPassword(email: string): Promise<void> {
    const params = new URLSearchParams();
    params.append('client_secret', this.configService.getConfig('clientSecret'));

    return this.http
      .post(this.configService.getUrl('resetPassword'), email, {
        headers: this.getXWWWFromHeader(),
      })
      .toPromise()
      .then(() => null);
  }

  public renewTokens(): Observable<string> {
    return Observable.fromPromise(this.storageService.get('refreshToken')).pipe(
      flatMap((refreshToken: string) => {
        if (refreshToken === null) {
          return Promise.reject(null);
        }

        const params = new URLSearchParams();
        params.append('client_secret', this.configService.getConfig('clientSecret'));
        params.append('client_id', this.configService.getConfig('clientId'));
        params.append('grant_type', 'refresh_token');
        params.append('refresh_token', refreshToken);
        return this.http
          .post(this.configService.TOKEN_URL, params.toString(), {
            headers: this.getXWWWFromHeader(),
          })
          .toPromise();
      }),
      tap((response: any) => {
        this.storageService.set('refreshToken', response.refresh_token);
      }),
      mergeMap((response) => this.storageService.set('accessToken', response.access_token)),
      catchError(() => {
        this.logout();
        return Observable.throw('[AuthService] Renew token is invalid');
      }),
      tap(() => console.log('[AuthService] Token refreshed')),
      shareReplay(),
    );
  }

  private getXWWWFromHeader(): HttpHeaders {
    return new HttpHeaders({
      'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
    });
  }

  public getState(): Observable<boolean> {
    return this._stateSubject;
  }
}



// WEBPACK FOOTER //
// ./src/modules/core/services/authentication-service/authentication-service.ts